import React, { useEffect, useState, useMemo, ChangeEvent, useCallback } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from "react-helmet";
import PropertyCard from "../components/property/PropertyCard";
import { useInView } from "react-intersection-observer";
import "../App.css";
import { Filter, getProperties, Property } from "../utils/properties";
import useThemeStore from "../store/themeStore";
import { ThemeProps } from "../utils/theme";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faCopy, faHeart, faKey, faBuilding } from '@fortawesome/free-solid-svg-icons';
import { FloatingWhatsApp } from 'react-floating-whatsapp';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import FeatureEnquiryModal from "./components/FeatureEnquiryModal";
import ReactDOM from 'react-dom';
import { LazyLoadImage } from "react-lazy-load-image-component";
import { CustomCard } from "./components/FeaturedCardsCarousel";
import backgroundPattern from "../images/vesta/vesta-property-pattern-01-teal-bg.svg";
import ListView from '../components/property/ListView';
import ViewModeSelector from '../components/ViewModeSelector';
import DetailView from '../components/property/DetailView';
import useTokenStore from "../store/store";
import SearchComponent from './components/SearchComponent';
import useCardList from '../data/useCardList';
import PropertyMap from '../components/property/PropertyMap';
import FavoritesModal from './components/FavoritesModal';
import ComingSoonModal from './components/ComingSoonModal';
import { useFavoritesStore } from '../store/favoritesStore';
import { isLocalhost } from "../utils/isLocalhost";
import toCDNImage from "../utils/toCDNImage";

function PropertyListing() {
  const navigate = useNavigate();
  const location = useLocation();
  const getToken = useTokenStore((state) => state.token);
  const cardList = useCardList();
  const isAuthenticated = !!getToken; // Boolean conversion of token to determine auth state

  const [showComingSoon, setShowComingSoon] = useState(false);

  // Define the number of properties to show for non-authenticated users
  const NON_AUTH_PROPERTY_LIMIT = 3;

  // Parse URL parameters
  const params = new URLSearchParams(location.search);

  // Initialize state variables from URL parameters
  const [region, setRegion] = useState(params.get('region') || '');
  const [strategy, setStrategy] = useState(params.get('strategy') || '');
  const [propertyType, setPropertyType] = useState(params.get('propertyType') || '');
  const [minValue, set_minValue] = useState(params.get('minValue') ? Number(params.get('minValue')) : 0);
  const [maxValue, set_maxValue] = useState(params.get('maxValue') ? Number(params.get('maxValue')) : 1000000000);
  const [isReserved, setIsReserved] = useState(params.get('isReserved') === 'true');
  const [showSold, setShowSold] = useState(params.get('showSold') === 'true');
  const [searchKeyword, setSearchKeyword] = useState(params.get('searchKeyword') || '');
  const [debouncedSearchKeyword, setDebouncedSearchKeyword] = useState(params.get('searchKeyword') || '');
  const [sortDate, setSortDate] = useState(params.get('sortDate') || 'Latest');
  const [sortPrice, setSortPrice] = useState(params.get('sortPrice') || 'By Price');
  const [sortYield, setSortYield] = useState(params.get('sortYield') || 'By Yield');
  const [sortDiscount, setSortDiscount] = useState(params.get('sortDiscount') || 'By Discount');
  const [activeSort, setActiveSort] = useState<string | null>(() => {
    if (params.get('sortDate') && params.get('sortDate') !== 'By Date') {
      return 'date';
    } else if (params.get('sortPrice') && params.get('sortPrice') !== 'By Price') {
      return 'price';
    } else if (params.get('sortYield') && params.get('sortYield') !== 'By Yield') {
      return 'yield';
    } else if (params.get('sortDiscount') && params.get('sortDiscount') !== 'By Discount') {
      return 'discount';
    } else {
      return 'date'; // default activeSort
    }
  });

  const [properties, setProperties] = useState<Property[]>([]);
  const [filterProperties, setFilterProperties] = useState<Filter | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(30);
  const [selectedProductId, selectedProductIdSet] = useState<string>("");
  const [viewMode, setViewMode] = useState('grid');
  const [sortConfig] = useState<{ key: keyof Property | null, direction: 'ascending' | 'descending' }>({ key: null, direction: 'ascending' });
  const [loadFilter] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const getThemes = useThemeStore((state) => state.theme as unknown as ThemeProps);

  // New state for tracking the scroll position when login modal is shown
  const [, setScrollPositionBeforeLogin] = useState(0);

  const [isFavoritesModalOpen, setIsFavoritesModalOpen] = useState(false);
  const [isAsteroomModalOpen, setIsAsteroomModalOpen] = useState(false);
  const [selectedAsteroomUrl, setSelectedAsteroomUrl] = useState<string>('');

  // Add floating buttons state
  const [, setIsVisible] = useState(false);

  // Add scroll handler for floating buttons
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsVisible(scrollPosition > 100); // Show buttons after scrolling 100px
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const { fetchFavorites } = useFavoritesStore();

  // Add this new useEffect to fetch favorites
  useEffect(() => {
    if (isAuthenticated) {
      fetchFavorites();
    }
  }, [isAuthenticated, fetchFavorites]);

  useEffect(() => {
    const intendedPropertyId = sessionStorage.getItem('intendedPropertyId');
    const storedFilterState = sessionStorage.getItem('filterState');
  
    if (isAuthenticated && intendedPropertyId) {
      sessionStorage.removeItem('intendedPropertyId');
      navigate(`/listing/${intendedPropertyId}`);
    } else if (storedFilterState) {
      const filterState = JSON.parse(storedFilterState);
      setRegion(filterState.region || "");
      setStrategy(filterState.strategy || "");
      setPropertyType(filterState.propertyType || "");
      set_minValue(filterState.minValue || 0);
      set_maxValue(filterState.maxValue || 1000000000);
      setIsReserved(filterState.isReserved || false);
      setShowSold(filterState.showSold || false);
      setSortDate(filterState.sortDate || "Latest");
      setSortPrice(filterState.sortPrice || "By Price");
      setSortYield(filterState.sortYield || "By Yield");
      setSortDiscount(filterState.sortDiscount || "By Discount");
      setSearchKeyword(filterState.searchKeyword || "");
      setActiveSort(filterState.activeSort || "date");
  
      sessionStorage.removeItem('filterState');
    }
  }, [isAuthenticated, navigate]);

  const handleCardClick = (id: string) => {
    if (isAuthenticated) {
      navigate(`/listing/${id}`);
    } else {
      // Store the current scroll position
      setScrollPositionBeforeLogin(window.pageYOffset);
      
      // Store the current filter state
      const filterState = {
        region,
        strategy,
        propertyType,
        minValue,
        maxValue,
        isReserved,
        showSold,
        sortDate,
        sortPrice,
        sortYield,
        sortDiscount,
        searchKeyword,
        activeSort
      };
      sessionStorage.setItem('filterState', JSON.stringify(filterState));

      // Navigate to login with redirectTo parameter
      navigate(`/login?redirectTo=/listing/${id}`);
    }
  };

  const BlurOverlayStyles = () => (
    <style>{`
      .blur-overlay {
        position: absolute;
        inset: 0;
        background-color: rgba(0, 0, 0, 0.7);
        backdrop-filter: blur(8px);
        -webkit-backdrop-filter: blur(8px);
        z-index: 999;
      }
      
      @supports not ((backdrop-filter: blur(8px)) or (-webkit-backdrop-filter: blur(8px))) {
        .blur-overlay {
          background-color: rgba(0, 0, 0, 0.85);
        }
      }
      
      .login-card {
        position: relative;
        z-index: 1000;
        background-color: white;
        border-radius: 0.5rem;
        padding: 1.5rem;
        max-width: 400px;
        width: 90%;
        text-align: center;
        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
      }
      
        .list-blur-container, .detail-blur-container, .grid-blur-container, .map-blur-container {
        position: relative;
        width: 100%;
        overflow: hidden;
      }

      .list-blur-overlay, .detail-blur-overlay, .map-blur-overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0) 30%, rgba(0, 0, 0, 0.8) 80%);
        z-index: 10;
        pointer-events: none;
      }
    `}</style>
  );

  const handleHover = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, hoverColor: string) => {
    // Toggle between normal and hover colors
    const hoverBg = hoverColor || getThemes.color1;
    const originalBg = getThemes.color2;
    
    // If mouse over, switch to hover color
    if (e.type === 'mouseover') {
      e.currentTarget.style.background = hoverBg;
      // Set appropriate text color based on background
      e.currentTarget.style.color = isWhiteOrShade(hoverBg) ? "#000000" : "#ffffff";
      e.currentTarget.style.border = isWhiteOrShade(hoverBg) ? "1px solid #000000" : "none";
    } 
    // If mouse out, switch back to original color
    else {
      e.currentTarget.style.background = originalBg;
      // Set appropriate text color based on background
      e.currentTarget.style.color = isWhiteOrShade(originalBg) ? "#000000" : "#ffffff";
      e.currentTarget.style.border = isWhiteOrShade(originalBg) ? "1px solid #000000" : "none";
    }
  };

  const [featuredCards, setFeaturedCards] = useState(cardList);

  const shuffleFeaturedCards = useCallback(() => {
    setFeaturedCards([...cardList].sort(() => Math.random() - 0.5));
  }, [cardList]);

  useEffect(() => {
    const interval = setInterval(shuffleFeaturedCards, 15000);
    return () => clearInterval(interval);
  }, [shuffleFeaturedCards]);

  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [animateIcons, setAnimateIcons] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState('Copy Link');

  useEffect(() => {
    if (isShareModalOpen) {
      setAnimateIcons(true);
      setTimeout(() => setAnimateIcons(false), 800); // duration of pulse animation
    }
  }, [isShareModalOpen]);

  const openShareModal = (property: Property) => {
    setSelectedProperty(property);
    setIsShareModalOpen(true);
  };

  const closeShareModal = () => {
    setIsShareModalOpen(false);
    setSelectedProperty(null);
  };

  const generatePropertyLink = (propertyId: string) => {
    const baseUrl = window.location.origin; 
    return `${baseUrl}/listing/${propertyId}`;
  };

  const handleCopyLink = () => {
    if (!selectedProperty) return;
    const link = generatePropertyLink(selectedProperty._id);
    navigator.clipboard.writeText(link);
    setCopyFeedback('Link Copied');
    setTimeout(() => {
      setCopyFeedback('Copy Link');
      closeShareModal();
    }, 2000);
  };

  const handleWhatsAppShare = () => {
    if (!selectedProperty) return;
    const link = generatePropertyLink(selectedProperty._id);
    window.open(`https://wa.me/?text=${encodeURIComponent(link)}`, '_blank');
    closeShareModal();
  };

  const handleEmailShare = () => {
    if (!selectedProperty) return;
    const link = generatePropertyLink(selectedProperty._id);
    window.location.href = `mailto:?subject=Check out this property&body=${encodeURIComponent(link)}`;
    closeShareModal();
  };

  const handleAsteroomClick = (asteroomUrl: string) => {
    setSelectedAsteroomUrl(asteroomUrl);
    setIsAsteroomModalOpen(true);
  };

  const handleRegionChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const selectedRegion = e.target.value;
    setRegion(selectedRegion);
  };

  // Optimize search debouncing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchKeyword(searchKeyword);
    }, 500); // Increased debounce time to 500ms for better performance

    return () => clearTimeout(timer);
  }, [searchKeyword]);

  // Memoize the search handler to prevent unnecessary re-renders
  const handleSearchInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchKeyword(e.target.value);
  }, []);

  // Optimize the properties fetching effect
  useEffect(() => {
    let isMounted = true;
    const fetchProperties = async () => {
      setLoading(true);
      const filter = {
        ...(region && { region }),
        ...(propertyType && { propertyType }),
        ...(strategy && { strategy }),
        ...(minValue !== 0 && { minPrice: minValue }),
        ...(maxValue !== 1000000000 && { maxPrice: maxValue }),
        ...(debouncedSearchKeyword && { searchKeyword: debouncedSearchKeyword }),
        ...(sortDate !== "By Date" && { sortDate }),
        ...(sortPrice !== "By Price" && { sortPrice }),
        ...(sortYield !== "By Yield" && { sortYield }),
        ...(sortDiscount !== "By Discount" && { sortDiscount }),
        includeReserved: isReserved,
        includeSold: showSold,
      };

      try {
        const pr = await getProperties({ ...filter, page: 1, limit: limit });
        if (isMounted) {
          // Filter out reserved and inLegals properties if isReserved is false
          const filteredProperties = isReserved ? pr.results : pr.results.filter((prop: Property) => !prop.reserved && prop.publishStatus !== 'inLegals');
          setProperties(filteredProperties.map((p) => ({ ...p, buildingUnits: Number(p.buildingUnits ?? 0) || 0 })));
          setFilterProperties(pr.filter);
          setPage(1);
          setHasMore(pr.pagination.next !== null);
        }
      } catch (error) {
        console.error('Error fetching properties:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
  
    fetchProperties();
    
    return () => {
      isMounted = false;
    };
  }, [
    region,
    propertyType,
    strategy,
    minValue,
    maxValue,
    debouncedSearchKeyword, // Using debounced value instead of searchKeyword
    sortDate,
    sortPrice,
    sortYield,
    sortDiscount,
    isReserved,
    showSold,
    limit,
  ]);

  const viewMore = useCallback(async () => {
    if (properties && properties.length > 0 && !loadingMore && hasMore) {
      setLoadingMore(true);
      const filter = {
        ...(region && { region }),
        ...(propertyType && { propertyType }),
        ...(strategy && { strategy }),
        ...(minValue !== 0 && { minPrice: minValue }),
        ...(maxValue !== 1000000000 && { maxPrice: maxValue }),
        ...(searchKeyword && { searchKeyword }),
        ...(sortDate !== "By Date" && { sortDate }),
        ...(sortPrice !== "By Price" && { sortPrice }),
        ...(sortYield !== "By Yield" && { sortYield }),
        ...(sortDiscount !== "By Discount" && { sortDiscount }),
        includeReserved: isReserved,
        includeSold: showSold,
      };

      const pr = await getProperties({ ...filter, page: page + 1, limit: limit });
      if (pr.results && pr.results.length > 0) {
        setProperties(prevProperties => [...prevProperties, ...pr.results.map((p) => ({ ...p, buildingUnits: Number(p.buildingUnits ?? 0) || 0 }))]);
        setPage(prevPage => prevPage + 1);
        setHasMore(pr.pagination.next !== null);
      } else {
        setHasMore(false);
      }
      setLoadingMore(false);
    }
  }, [
    properties,
    loadingMore,
    hasMore,
    region,
    propertyType,
    strategy,
    minValue,
    maxValue,
    searchKeyword,
    sortDate,
    sortPrice,
    sortYield,
    sortDiscount,
    isReserved,
    showSold,
    page,
    limit,
  ]);

  const sortPriceAction = () => {
    setSortPrice("Lowest");
    setSortDate("By Date");
    setSortYield("By Yield");
    setSortDiscount("By Discount");
    setActiveSort("price");
  };

  const sortDateAction = () => {
    setSortDate("Latest");
    setSortPrice("By Price");
    setSortYield("By Yield");
    setSortDiscount("By Discount");
    setActiveSort("date");
  };

  const sortYieldAction = () => {
    setSortYield("Highest");
    setSortDate("By Date");
    setSortPrice("By Price");
    setSortDiscount("By Discount");
    setActiveSort("yield");
  };

  const sortDiscountAction = () => {
    setSortDiscount("Highest");
    setSortDate("By Date");
    setSortYield("By Yield");
    setSortPrice("By Price");
    setActiveSort("discount");
  };

  const clearFilters = async () => {
    const scrollPosition = window.pageYOffset;
  
    setRegion("");
    setStrategy("");
    setPropertyType("");
    set_minValue(filterProperties?.minPrice ?? 0);
    set_maxValue(filterProperties?.maxPrice ?? 1000000000);
    setSortDate("Latest");
    setSortPrice("By Price");
    setSortYield("By Yield");
    setSortDiscount("By Discount");
    setIsReserved(false);
    setShowSold(false);

    // Clear search states
    setSearchKeyword("");
    setDebouncedSearchKeyword("");

    // Set activeSort to "date" to highlight the "Newest Date" button
    setActiveSort("date");

    setLoading(true);
    const pr = await getProperties({ page: 1, limit: limit, sortDate: "Latest" });
    
    // Filter out reserved and inLegals properties after fetching
    const filteredProperties = pr.results.filter((prop: Property) => !prop.reserved && prop.publishStatus !== 'inLegals');
    
    setProperties(filteredProperties.map((p) => ({ ...p, buildingUnits: Number(p.buildingUnits ?? 0) || 0 })));
    setFilterProperties(pr.filter);
    setLoading(false);
    setPage(1);
    setHasMore(pr.pagination.next !== null);
  
    setTimeout(() => {
      window.scrollTo(0, scrollPosition);
    }, 0);
  };

  const [viewMoreButtonRef, inView] = useInView({});

  useEffect(() => {
    if (inView) {
      viewMore();
    }
  }, [inView, viewMore]);

  interface PropsLoading {
    color: string;
  }
  
  const LoadingDiv = ({ color }: PropsLoading) => {
    return (
      <div className="h-10 w-10">
        <svg version="1.1" id="L9" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 100 100" enableBackground="new 0 0 0 0" xmlSpace="preserve">
          <path fill={color} d="M73,50c0-12.7-10.3-23-23-23S27,37.3,27,50 M30.9,50c0-10.5,8.5-19.1,19.1-19.1S69.1,39.5,69.1,50">
            <animateTransform attributeName="transform" attributeType="XML" type="rotate" dur="1s" from="0 50 50" to="360 50 50" repeatCount="indefinite" />
          </path>
        </svg>
      </div>
    );
  };
   
  const sortedProperties = useMemo(() => {
    let sortableItems = [...properties];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        const aValue = a[sortConfig.key as keyof Property];
        const bValue = b[sortConfig.key as keyof Property];

        if (aValue === null || bValue === null || aValue === undefined || bValue === undefined) {
          return 0;
        }

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortConfig.direction === 'ascending'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortConfig.direction === 'ascending'
            ? aValue - bValue
            : bValue - aValue;
        }

        return 0;
      });
    }
    return sortableItems;
  }, [properties, sortConfig]);

  function requestSort(key: keyof Property): void {
    // This function implementation can be added if needed
  }

  // Helper function to clean and deduplicate options
  const cleanOptions = (options: string[]) => {
    const uniqueOptions = new Set<string>();
    options.forEach(option => {
      if (option.trim() !== "" && option.toLowerCase() !== "block of three flats") {
        uniqueOptions.add(option.trim().toLowerCase());
      }
    });

    return Array.from(uniqueOptions).map(option => {
      if (option === "hmo") {
        return "HMO";
      }
      if (option === "semi-detached" || option === "semi-detached house") {
        return "Semi-Detached House";
      }
      if (option === "terraced" || option === "terraced house") {
        return "Terraced House";
      }
      return option.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    }).filter((value, index, self) => self.indexOf(value) === index); // Ensuring unique options
  };

  // Utility function to check for white or near-white shades
  const isWhiteOrShade = (color: string) => {
    if (color.toLowerCase() === "white" || color === "rgba(255,255,255,1)") return true;
    if (/^#fff[a-f0-9]{0,3}$/i.test(color)) return true;
  
    const rgba = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+),?\s*([\d.]+)?\)/);
    if (rgba) {
      const [, r, g, b] = rgba.map(Number);
      return r >= 240 && g >= 240 && b >= 240;
    }
  
    return false;
  };
  
  // Usage in JSX
  const borderColor = getThemes && isWhiteOrShade(getThemes.color1) ? getThemes.color2 : getThemes.color1;

  const handleLoginClick = () => {
    // Store the current scroll position before navigating
    setScrollPositionBeforeLogin(window.pageYOffset);
    
    // Store the current filter state
    const filterState = {
      region,
      strategy,
      propertyType,
      minValue,
      maxValue,
      isReserved,
      showSold,
      sortDate,
      sortPrice,
      sortYield,
      sortDiscount,
      searchKeyword,
      activeSort,
      viewMode // Store the current view mode
    };
    sessionStorage.setItem('filterState', JSON.stringify(filterState));

    navigate('/login');
  };

  return (
    <div className="PropertiesListing pt-20">
      <Helmet>
        <title>Property Listing</title>
      </Helmet>
      {/* <PropertyNavigationBar currentPage="find" /> */}
      <div className="pb-10 mt-28">
        <div className="border max-w-7xl m-auto p-5 rounded-xl shadow-md">
          <div className="flex border-b mb-4">
            <button
              className="px-6 py-3 text-sm font-semibold border-b-2 -mb-px"
              style={{ borderColor, color: borderColor }}
            >
              <FontAwesomeIcon icon={faKey} className="mr-2" />Off Market
            </button>
            <button
              className="px-6 py-3 text-sm font-semibold text-gray-500 hover:text-gray-700 border-b-2 border-transparent -mb-px"
              onClick={() => setShowComingSoon(true)}
            >
              <FontAwesomeIcon icon={faBuilding} className="mr-2" />New Build
            </button>
          </div>
          <div className="grid lg:grid-cols-4 grid-cols-1 lg:gap-x-2 gap-1 max-w-7xl m-auto justify-center items-center lg:px-0 px-5">
            {/* Region Select */}
            <div className="px-2">
              <label
                htmlFor="hs-select-label-region"
                className="block text-base font-normal mb-2 text-primarycolor"
                >
                Region
              </label>
              <div className="relative">
                <select
                  id="hs-select-label-region"
                  className="py-3 px-4 pr-9 block w-full border-primarycolor border-2 rounded-md text-sm bg-white-f2f9fa appearance-none"
                  style={{ borderColor }}
                  value={region}
                  onChange={handleRegionChange}
                >
                  <option value="">Select</option>
                  {properties &&
                    filterProperties?.regions.map((ele, index) => (
                      <option key={index} value={ele}>
                        {ele}
                      </option>
                    ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Strategy Select */}
            <div className="px-2">
              <label
                htmlFor="hs-select-label-strategy"
                className="block text-base font-normal mb-2 text-primarycolor"
                >
                Strategy
              </label>
              <div className="relative">
                <select
                  id="hs-select-label-strategy"
                  className="py-3 px-4 pr-9 block w-full border-primarycolor border-2 rounded-md text-sm bg-white-f2f9fa appearance-none"
                  style={{ borderColor }}
                  value={strategy}
                  onChange={(e) => {
                    setStrategy(e.target.value);
                  }}>
                  <option value="">Select</option>
                  {properties &&
                    cleanOptions(filterProperties?.strategy ?? []).map((ele, index) => (
                      <option key={index} value={ele}>
                        {ele}
                      </option>
                    ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Property Type Select */}
            <div className="px-2">
              <label
                htmlFor="hs-select-label-property-type"
                className="block text-base font-normal mb-2 text-primarycolor"
                >
                Property Type
              </label>
              <div className="relative">
                <select
                  id="hs-select-label-property-type"
                  className="py-3 px-4 pr-9 block w-full border-primarycolor border-2 rounded-md text-sm bg-white-f2f9fa appearance-none"
                  style={{ borderColor }}
                  value={propertyType}
                  onChange={(e) => {
                    setPropertyType(e.target.value);
                  }}>
                  <option value="">Select</option>
                  {properties &&
                    cleanOptions(filterProperties?.propertyType ?? []).map((ele, index) => (
                      <option key={index} value={ele}>
                        {ele}
                      </option>
                    ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Price Range Select */}
            <div className="px-2">
              <label
                htmlFor="hs-select-label-price"
                className="block text-base font-normal mb-2 text-primarycolor"
                >
                Price
              </label>
              <div className="relative">
                <select
                  id="hs-select-label-price"
                  className="py-3 px-4 pr-9 block w-full border-primarycolor border-2 rounded-md text-sm bg-white-f2f9fa appearance-none"
                  style={{ borderColor }}
                  value={`${minValue}-${maxValue}`}
                  onChange={(e) => {
                    const [min, max] = e.target.value.split('-').map(Number);
                    set_minValue(min);
                    set_maxValue(max);
                  }}>
                  <option value="0-999999999">Any</option>
                  <option value="0-125000">Up to £125,000</option>
                  <option value="125000-250000">£125,000 - £250,000</option>
                  <option value="250000-500000">£250,000 - £500,000</option>
                  <option value="500000-1000000">£500,000 - £1,000,000</option>
                  <option value="1000000-5000000">£1,000,000 - £5,000,000</option>
                  <option value="5000000-999999999">£5,000,000+</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-5 flex flex-col md:flex-row items-center justify-between px-2">
            {/* Sorting Section */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4 items-center">
                <h3 className="text-gray-700 font-medium col-span-2 md:col-span-1">Sort By:</h3>

                {/* Date Sort */}
                <button
                    onClick={sortDateAction}
                    className="py-2 px-2 inline-flex justify-center items-center gap-2 rounded-md border font-medium shadow-sm align-middle hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white transition-all text-sm"
                    style={{
                        backgroundColor: activeSort === "date" ? getThemes.color2 : "white",
                        borderColor: activeSort === "date" ? getThemes.color2 : "gray",
                        color: activeSort === "date" ? "white" : "black",
                    }}
                >
                    Newest Date
                </button>

                {/* Price Sort */}
                <button
                    onClick={sortPriceAction}
                    className="py-2 px-3 inline-flex justify-center items-center gap-2 rounded-md border font-medium shadow-sm align-middle hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white transition-all text-sm"
                    style={{
                        backgroundColor: activeSort === "price" ? getThemes.color2 : "white",
                        borderColor: activeSort === "price" ? getThemes.color2 : "gray",
                        color: activeSort === "price" ? "white" : "black",
                    }}
                >
                    Lowest Price
                </button>

                {/* Yield Sort */}
                <button
                    onClick={sortYieldAction}
                    className="py-2 px-3 inline-flex justify-center items-center gap-2 rounded-md border font-medium shadow-sm align-middle hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white transition-all text-sm"
                    style={{
                        backgroundColor: activeSort === "yield" ? getThemes.color2 : "white",
                        borderColor: activeSort === "yield" ? getThemes.color2 : "gray",
                        color: activeSort === "yield" ? "white" : "black",
                    }}
                >
                    Highest Yield
                </button>

                {/* Discount Sort */}
                <button
                    onClick={sortDiscountAction}
                    className="py-2 px-3 inline-flex justify-center items-center gap-2 rounded-md border font-medium shadow-sm align-middle hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white transition-all text-sm"
                    style={{
                        backgroundColor: activeSort === "discount" ? getThemes.color2 : "white",
                        borderColor: activeSort === "discount" ? getThemes.color2 : "gray",
                        color: activeSort === "discount" ? "white" : "black",
                    }}
                >
                    Highest Discount
                </button>

                {/* Clear Filters Button */}
                <button
                    onClick={clearFilters}
                    className={`py-2 px-3 inline-flex justify-center items-center gap-2 rounded-md border font-medium bg-white text-gray-700 shadow-sm align-middle hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white focus:ring-blue-600 transition-all text-sm
                    ${!activeSort ? 'opacity-50 cursor-not-allowed' : ''}`}
                    disabled={!activeSort}
                >
                    Clear Filters
                </button>
            </div>

            {/* Search Bar */}
            <div className="px-2 pt-5 sm:pt-0">
              <SearchComponent
                searchKeyword={searchKeyword}
                handleSearchInputChange={handleSearchInputChange}
                getThemes={getThemes}
              />
            </div>
          </div>
        </div>
      </div>

      {!loading && (
        <div className="flex flex-wrap justify-between m-auto max-w-7xl mb-5 pl-2">
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                const next = !isReserved;
                setIsReserved(next);
                if (next) setShowSold(false);
              }}
              className="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium cursor-pointer transition-all duration-200"
              style={{
                backgroundColor: isReserved ? getThemes.color2 : 'white',
                color: isReserved
                  ? (isWhiteOrShade(getThemes.color2) ? '#000' : '#fff')
                  : '#374151',
                border: isReserved ? 'none' : '1px solid #D1D5DB',
              }}
            >
              {isReserved && (
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M11.5 3.5L5.5 10L2.5 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
              Show Reserved
            </button>

            <button
              onClick={() => {
                const next = !showSold;
                setShowSold(next);
                if (next) setIsReserved(false);
              }}
              className="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium cursor-pointer transition-all duration-200"
              style={{
                backgroundColor: showSold ? getThemes.color2 : 'white',
                color: showSold
                  ? (isWhiteOrShade(getThemes.color2) ? '#000' : '#fff')
                  : '#374151',
                border: showSold ? 'none' : '1px solid #D1D5DB',
              }}
            >
              {showSold && (
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M11.5 3.5L5.5 10L2.5 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
              Show Sold
            </button>
          </div>
          <div className="flex items-center gap-3">
            {isAuthenticated && (
              <button
                onClick={() => setIsFavoritesModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-md font-semibold cursor-pointer transition-transform duration-300 ease-in-out transform hover:scale-105"
                style={{
                  background: getThemes.color2,
                  color: isWhiteOrShade(getThemes.color2) ? "#000000" : "#ffffff",
                  border: isWhiteOrShade(getThemes.color2) ? "1px solid #000000" : "none",
                }}
                onMouseOver={(e) => handleHover(e, getThemes.color1)}
                onMouseOut={(e) => handleHover(e, getThemes.color2)}
              >
                <FontAwesomeIcon icon={faHeart} />
                Favorites
              </button>
            )}
            <ViewModeSelector viewMode={viewMode} setViewMode={setViewMode} getThemes={getThemes} />
          </div>
        </div>
      )}

      <div className="max-w-7xl m-auto pb-10" style={{ overflowX: "hidden" }}>
        <BlurOverlayStyles />
        {isModalOpen && <FeatureEnquiryModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} productId={selectedProductId} />}
        {isModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-[9998] transition-opacity duration-300" />
        )}
        {!loadFilter && (
          <>
            {loading ? (
              <div className="h-screen flex top-[50%] bottom-0 m-auto justify-center items-center">
                <LoadingDiv color={getThemes && getThemes.color1 === "rgba(255,255,255,1)" ? "rgba(180,180,180,1)" : getThemes.color1} />
              </div>
            ) : (
              <>
                {/* GRID VIEW */}
                {viewMode === 'grid' && (
                  <div className="grid-blur-container">
                    <div className="grid lg:grid-cols-3 grid-cols-1 w-full m-auto gap-3 lg:px-0">
                      {sortedProperties.map((ele, index) => (
                        <React.Fragment key={index}>
                          <div className="relative">
                            <PropertyCard
                              discount={ele.discountLabel as unknown as string}
                              discountLabel={ele.discountLabel}
                              id={ele._id}
                              available={!ele.reserved}
                              publishStatus={ele.publishStatus || 'available'}
                              image={toCDNImage(ele.images && ele.images.length > 0 ? ele.images[0].url : "")}
                              streetName={ele.propertyOf}
                              price={ele.price as unknown as string}
                              bedrooms={ele.bedrooms as unknown as string}
                              buildingUnits={Number(ele.buildingUnits ?? 0) || 0}
                              value={ele.estimatedValue as unknown as string}
                              rent={ele.averageRent as unknown as string}
                              netCashFlow={ele.netCashflow as unknown as string}
                              grossYield={ele.rentGrossYield as unknown as string}
                              strategy={ele.strategy as unknown as string}
                              occupancy={ele.occupancy}
                              tenureType={ele.tenureType}
                              floorsqft={ele.floorsqft}
                              region={(ele as Property).region}
                              investment={ele.investment as unknown as string}
                              asteroomUrl={ele.asteroomUrl}
                              onShareClick={() => openShareModal(ele)}
                              onClick={() => handleCardClick(ele._id)}
                              onAsteroomClick={() => ele.asteroomUrl && handleAsteroomClick(ele.asteroomUrl)}
                            />
                            {/* Show login overlay for non-authenticated users after the third property */}
                            {!isAuthenticated && index >= NON_AUTH_PROPERTY_LIMIT && (
                              <div 
                                className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-xl z-10"
                                style={{ backdropFilter: 'blur(4px)' }}
                              >
                                <div className="text-center p-6 bg-white rounded-lg shadow-lg max-w-xs" style={{ backgroundColor: 'white' }}>
                                  <h3 className="text-lg font-bold mb-3 text-gray-800">Sign In to View More Properties</h3>
                                  <p className="text-sm text-gray-600 mb-4">
                                    Create an account or sign in to view our full range of exclusive properties.
                                  </p>
                                  <button 
                                    onClick={handleLoginClick}
                                    className="w-auto sm:w-auto py-2 px-6 rounded-md font-semibold cursor-pointer transition-transform duration-300 ease-in-out transform hover:scale-105"
                                    style={{
                                      background: getThemes.color2,
                                      color: isWhiteOrShade(getThemes.color2) ? "#000000" : "#ffffff",
                                      border: isWhiteOrShade(getThemes.color2) ? "1px solid #000000" : "none",
                                    }}
                                    onMouseOver={(e) => handleHover(e, getThemes.color1)}
                                    onMouseOut={(e) => handleHover(e, getThemes.color2)}
                                  >
                                    Sign In Now
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                          {(index + 1) % 10 === 0 && featuredCards[Math.floor(index / 10) % featuredCards.length] && (
                            <div
                              key={`featured-${index}`}
                              className="shadow-md rounded-lg overflow-hidden"
                              style={{
                                backgroundImage: `url(${backgroundPattern})`,
                                backgroundRepeat: 'repeat',
                                backgroundSize: '60px 60px',
                              }}
                            >
                              <CustomCard
                                isFirst={false} 
                                isLast={false} 
                                {...featuredCards[Math.floor(index / 10) % featuredCards.length]}
                                onOpenModal={(productId) => {
                                  selectedProductIdSet(productId);
                                  setIsModalOpen(true);
                                }}
                              />
                            </div>
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                    
                    {/* Only show overlay after displaying NON_AUTH_PROPERTY_LIMIT properties for non-authenticated users */}
                    {!isAuthenticated && (
                      <div className="mt-6 flex justify-center">
                        <div className="text-center p-6 bg-white rounded-lg border border-gray-200 shadow-lg max-w-md" style={{ backgroundColor: 'white' }}>
                          <h3 className="text-xl font-bold mb-3 text-gray-800">
                            Access All Properties
                          </h3>
                          <p className="mb-4 text-gray-600">
                            Sign in to browse our complete collection of premium investment properties.
                          </p>
                          <button 
                            onClick={handleLoginClick}
                            className="w-auto sm:w-auto py-2 px-6 rounded-md font-semibold cursor-pointer transition-transform duration-300 ease-in-out transform hover:scale-105"
                            style={{
                              background: getThemes.color2,
                              color: isWhiteOrShade(getThemes.color2) ? "#000000" : "#ffffff",
                              border: isWhiteOrShade(getThemes.color2) ? "1px solid #000000" : "none",
                            }}
                            onMouseOver={(e) => handleHover(e, getThemes.color1)}
                            onMouseOut={(e) => handleHover(e, getThemes.color2)}
                          >
                            Sign In Now
                          </button>
                        </div>
                      </div>
                    )}

                  </div>
                )}

                {/* LIST VIEW */}
                {viewMode === 'list' && (
                  <div className="list-blur-container">
                    <ListView
                      properties={sortedProperties.slice(0, isAuthenticated ? sortedProperties.length : NON_AUTH_PROPERTY_LIMIT)}
                      onRequestSort={requestSort}
                      onShareClick={openShareModal}
                    />
                    
                    {/* Overlay for non-authenticated users */}
                    {!isAuthenticated && (
                      <div className="mt-10">
                        {/* Login CTA */}
                        <div className="flex justify-center items-center pt-4">
                          <div className="text-center p-6 mb-5 bg-white rounded-lg shadow-lg max-w-md border border-gray-200">
                            <h3 className="text-xl font-bold mb-3 text-gray-800">
                              View All Available Properties
                            </h3>
                            <p className="mb-4 text-gray-600">
                              Sign in to browse our complete collection of premium investment properties.
                            </p>
                            <button 
                              onClick={handleLoginClick}
                              className="w-auto sm:w-auto py-2 px-6 rounded-md font-semibold cursor-pointer transition-transform duration-300 ease-in-out transform hover:scale-105"
                              style={{
                                background: getThemes.color2,
                                color: isWhiteOrShade(getThemes.color2) ? "#000000" : "#ffffff",
                                border: isWhiteOrShade(getThemes.color2) ? "1px solid #000000" : "none",
                              }}
                              onMouseOver={(e) => handleHover(e, getThemes.color1)}
                              onMouseOut={(e) => handleHover(e, getThemes.color2)}
                            >
                              Sign In Now
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                  </div>
                )}

                {/* DETAIL VIEW */}
                {viewMode === 'detail' && (
                  <div className="detail-blur-container">
                    <DetailView
                      properties={sortedProperties.slice(0, isAuthenticated ? sortedProperties.length : NON_AUTH_PROPERTY_LIMIT)}
                      onShareClick={openShareModal}
                      setIsModalOpen={setIsModalOpen}
                      selectedProductIdSet={selectedProductIdSet}
                    />
                    
                    {/* Overlay for non-authenticated users */}
                    {!isAuthenticated && (
                      <div className="mt-10">
                        {/* Login CTA */}
                        <div className="flex justify-center items-center pt-4">
                          <div className="text-center p-6 mb-5 bg-white rounded-lg shadow-lg max-w-md border border-gray-200">
                            <h3 className="text-xl font-bold mb-3 text-gray-800">
                              Sign In to Access All Properties
                            </h3>
                            <p className="mb-4 text-gray-600">
                              Create an account or sign in to access all our premium property listings.
                            </p>
                            <button 
                              onClick={handleLoginClick}
                              className="w-auto sm:w-auto py-2 px-6 rounded-md font-semibold cursor-pointer transition-transform duration-300 ease-in-out transform hover:scale-105"
                              style={{
                                background: getThemes.color2,
                                color: isWhiteOrShade(getThemes.color2) ? "#000000" : "#ffffff",
                                border: isWhiteOrShade(getThemes.color2) ? "1px solid #000000" : "none",
                              }}
                              onMouseOver={(e) => handleHover(e, getThemes.color1)}
                              onMouseOut={(e) => handleHover(e, getThemes.color2)}
                            >
                              Sign In Now
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                  </div>
                )}

                {/* MAP VIEW */}
                {viewMode === 'map' && (
                  <div className="map-blur-container">
                    <PropertyMap properties={sortedProperties.slice(0, isAuthenticated ? sortedProperties.length : NON_AUTH_PROPERTY_LIMIT)} />

                    {/* Login CTA for non-authenticated users */}
                    {!isAuthenticated && (
                      <div className="mt-10">
                        <div className="flex justify-center items-center pt-4">
                          <div className="text-center p-6 mb-5 bg-white rounded-lg shadow-lg max-w-md border border-gray-200">
                            <h3 className="text-xl font-bold mb-3 text-gray-800">
                              View All Properties on Map
                            </h3>
                            <p className="mb-4 text-gray-600">
                              Sign in to browse our complete collection of properties on the map.
                            </p>
                            <button
                              onClick={handleLoginClick}
                              className="w-auto sm:w-auto py-2 px-6 rounded-md font-semibold cursor-pointer transition-transform duration-300 ease-in-out transform hover:scale-105"
                              style={{
                                background: getThemes.color2,
                                color: isWhiteOrShade(getThemes.color2) ? "#000000" : "#ffffff",
                                border: isWhiteOrShade(getThemes.color2) ? "1px solid #000000" : "none",
                              }}
                              onMouseOver={(e) => handleHover(e, getThemes.color1)}
                              onMouseOut={(e) => handleHover(e, getThemes.color2)}
                            >
                              Sign In Now
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                  </div>
                )}

                {properties?.length === 0 && !loading && (
                  <div className="flex w-full justify-center items-center mt-10">
                    <h1 className="text-center">No Properties Found</h1>
                  </div>
                )}
              </>
            )}
          </>
        )}
        
        {loadFilter && (
          <div className="flex w-full justify-center items-center mt-10">
            <LoadingDiv color={getThemes && getThemes.color1 === "rgba(255,255,255,1)" ? "rgba(180,180,180,1)" : getThemes.color1} />
          </div>
        )}
        
        {!loading && !loadFilter && hasMore && isAuthenticated && (
          <div ref={viewMoreButtonRef} className="w-full flex justify-center items-center mt-6">
            <button className="w-full flex justify-center items-center">
              {loadingMore && <LoadingDiv color={getThemes && getThemes.color1 === "rgba(255,255,255,1)" ? "rgba(180,180,180,1)" : getThemes.color1} />}
            </button>
          </div>
        )}
      </div>
      
      {/* Floating WhatsApp Integration */}
      {isAuthenticated && !isLocalhost && (
        <div
          className="custom-floating-whatsapp-wrapper"
          style={{ 
            '--avatar-bg-color': getThemes?.color1,
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            zIndex: 9999
          } as React.CSSProperties}
        >
          <FloatingWhatsApp
            phoneNumber={getThemes?.adminPhoneNumber}
            accountName={`${getThemes?.adminFirstName} ${getThemes?.adminLastName}`}
            avatar={getThemes?.favIcon}
            className="custom-floating-whatsapp"
            statusMessage="Typically replies within 1 hour"
            chatMessage={getThemes?.name === 'TrustBricks' ? 'Hi, how can I help? You can also reach us on 020 3985 2580' : 'Hi, how can I help?'}
            placeholder="Type a message.."
            messageDelay={2}
            darkMode={false}
            allowClickAway
            allowEsc
            chatboxHeight={320}
            notification
            notificationDelay={60}
            notificationSound
            notificationLoop={1}
          />
        </div>
      )}

      {/* Standalone Heart Button */}
      {isAuthenticated && (
        <div className="fixed bottom-28 right-8 z-[9998]">
          <div className="relative group">
            <div
              className="absolute inset-0 rounded-full animate-ping"
              style={{
                backgroundColor: getThemes?.color2,
                opacity: 0.2,
                animationDuration: '2s',
              }}
            />
            <button
              onClick={() => setIsFavoritesModalOpen(true)}
              className="relative w-14 h-14 rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition duration-300"
              style={{
                backgroundColor: getThemes?.color2,
                color: isWhiteOrShade(getThemes?.color2) ? 'black' : getThemes?.txtColor,
              }}
            >
              <FontAwesomeIcon icon={faHeart} size="lg" />
            </button>
            <div
              className="absolute right-full mr-2 top-1/2 transform -translate-y-1/2 px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap"
              style={{ 
                backgroundColor: getThemes?.color2, 
                color: isWhiteOrShade(getThemes?.color2) ? 'black' : getThemes?.txtColor 
              }}
            >
              View Favourites
            </div>
          </div>
        </div>
      )}

      {/* Favorites Modal */}
      <FavoritesModal 
        isOpen={isFavoritesModalOpen} 
        onClose={() => setIsFavoritesModalOpen(false)}
        themeColor={getThemes.color1}
        textColor={getThemes.txtColor}
      />

      {/* Share Modal */}
      {isShareModalOpen && selectedProperty && ReactDOM.createPortal(
        <div className="fixed inset-0 flex items-center justify-center z-[9999]" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="bg-white rounded-lg p-6 w-96 relative" style={{ backgroundColor: 'white' }}>
            <style>
              {`
                @keyframes pulse {
                  0%, 100% { transform: scale(1); }
                  30% { transform: scale(1.2); }
                }
                .pulse {
                  animation: pulse 1s infinite;
                }
              `}
            </style>
            <span 
              onClick={closeShareModal}
              style={{
                position: "absolute",
                top: "10px",
                right: "10px",
                cursor: "pointer",
                fontSize: "20px",
                fontWeight: "bold",
                transition: "transform 0.2s",
                display: "inline-block",
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.3)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              &times;
            </span>
            <h3 className="text-2xl font-bold mb-4 text-center">Share Property</h3>
            <div className="flex items-center mb-4">
              {selectedProperty?.images && selectedProperty.images.length > 0 && (
                <LazyLoadImage
                  src={selectedProperty.images[0].url}
                  width={120}
                  height={60}
                  className="rounded mr-4 object-cover"
                  alt="Property Thumbnail"
                  style={{ width: '140px', height: '80px', objectFit: 'cover' }}
                />
              )}
              <div>
                <h4 className="font-medium">{selectedProperty?.propertyOf}</h4>
                <p className="text-gray-600">{selectedProperty?.propertyOf}</p>
              </div>
            </div>
            <p className="mb-4 text-center">
              You can share the following link for anyone to view this property
            </p>
            <div className="flex justify-around">
              <div className="flex flex-col items-center">
                <button
                  className={`focus:outline-none transition duration-300 ease-in-out transform hover:scale-110 ${animateIcons ? 'pulse' : ''}`}
                  onClick={handleCopyLink}
                >
                  <FontAwesomeIcon icon={faCopy} size="1x" className="mb-2" style={{ fontSize: '1.5rem' }} />
                </button>
                <span style={{ fontSize: '0.7rem' }}>{copyFeedback}</span>
              </div>

              <div className="flex flex-col items-center">
                <button
                  className={`focus:outline-none transition duration-300 ease-in-out transform hover:scale-110 ${animateIcons ? 'pulse' : ''}`}
                  onClick={handleWhatsAppShare}
                  style={{ 
                    width: '2rem', 
                    height: '2rem', 
                    paddingTop: "2px",
                    backgroundColor: '#25D366', 
                    borderRadius: '60%', 
                    justifyContent: 'center', 
                    alignItems: 'center' 
                  }}
                >
                  <FontAwesomeIcon icon={faWhatsapp} style={{ fontSize: '1.3rem', color: 'white' }} />
                </button>
                <span style={{ fontSize: '0.7rem' }}>Whatsapp</span>
              </div>

              <div className="flex flex-col items-center">
                <button
                  className={`focus:outline-none transition duration-300 ease-in-out transform hover:scale-110 ${animateIcons ? 'pulse' : ''}`}
                  onClick={handleEmailShare}
                >
                  <FontAwesomeIcon icon={faEnvelope} size="1x" className="mb-2" style={{ fontSize: '1.5rem' }} />
                </button>
                <span style={{ fontSize: '0.7rem' }}>Email</span>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Asteroom Modal */}
      {isAsteroomModalOpen && selectedAsteroomUrl && (
        <div 
          className="fixed inset-0 z-[99999] overflow-x-hidden overflow-y-auto"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
          onClick={(e: React.MouseEvent<HTMLDivElement>) => {
            if (e.target === e.currentTarget) {
              setIsAsteroomModalOpen(false);
            }
          }}
        >
          <div className="flex items-center justify-center min-h-screen p-2">
            <div 
              className="bg-white rounded-lg shadow-xl w-[95%] h-[95vh] max-w-7xl flex flex-col relative"
              style={{ backgroundColor: 'white' }}
            >
              <div className="flex justify-between items-center p-4 border-b border-gray-200">
                <h3 className="text-xl font-bold text-gray-800">Virtual Tour</h3>
                <button 
                  onClick={() => setIsAsteroomModalOpen(false)}
                  className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
                  style={{
                    transition: "transform 0.2s",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.3)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="flex-1 p-4" style={{ backgroundColor: 'white' }}>
                <iframe 
                  src={selectedAsteroomUrl}
                  className="w-full h-full border-0 rounded"
                  title="Virtual Tour"
                  allowFullScreen
                  style={{ backgroundColor: 'white' }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      <ComingSoonModal isOpen={showComingSoon} onClose={() => setShowComingSoon(false)} />
    </div>
  );
}

export default PropertyListing;